// src/pages/reports/OTSubmissionAuditReport.js

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  FileDownload as DownloadIcon,
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Timer as TimerIcon,
  CheckCircle as ApprovedIcon,
  PhoneIphone as MobileIcon,
  Build as ManualIcon,
  HourglassEmpty as PendingIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import "jspdf-autotable";

import { fetchOtSubmissionAuditReport } from "../../services/webReportsService";
import AppHeader from "../../components/AppHeader";
import { formatYangon } from "../../services/datetimeService";

const SOURCES = [
  { code: "", label: "All Sources" },
  { code: "MOBILE_CLAIM", label: "📱 Mobile App Claim" },
  { code: "HR_MANUAL", label: "🛠️ HR Direct Entry" },
  { code: "AUTO_CAPTURE", label: "⚡ System Auto-Capture" },
  { code: "PRE_PLANNED", label: "📋 Roster Planned OT" },
];

const STATUSES = [
  { code: "", label: "All Statuses" },
  { code: "submitted", label: "Submitted" },
  { code: "in_review", label: "In Review" },
  { code: "approved", label: "Approved" },
  { code: "rejected", label: "Rejected" },
];

const sourceColor = {
  MOBILE_CLAIM: "#3b82f6",
  MOBILE: "#3b82f6",
  HR_MANUAL: "#8b5cf6",
  AUTO_CAPTURE: "#eab308",
  PRE_PLANNED: "#06b6d4",
};

const statusColor = {
  submitted: "info",
  in_review: "warning",
  approved: "success",
  rejected: "error",
  pending: "warning",
};

const toDateKey = (date) => {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export default function OTSubmissionAuditReport() {
  const navigate = useNavigate();
  const tenant = useMemo(() => localStorage.getItem("tenant") || "", []);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [source, setSource] = useState("");
  const [status, setStatus] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [metrics, setMetrics] = useState({
    total_records: 0,
    total_requested_hours: 0,
    total_approved_hours: 0,
    mobile_claims_count: 0,
    mobile_claims_hours: 0,
    hr_manual_count: 0,
    hr_manual_hours: 0,
    auto_capture_hours: 0,
    pending_count: 0,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    const now = new Date();
    const from = new Date(now);
    from.setDate(now.getDate() - 30);

    setDateFrom(toDateKey(from));
    setDateTo(toDateKey(now));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(employeeSearch.trim());
    }, 350);

    return () => clearTimeout(timer);
  }, [employeeSearch]);

  const canSearch = useMemo(() => Boolean(dateFrom && dateTo), [dateFrom, dateTo]);

  const showMessage = useCallback((severity, message) => {
    setSnackbar({ open: true, severity, message });
  }, []);

  const loadReport = useCallback(async () => {
    if (!tenant) {
      showMessage("error", "Missing tenant configuration.");
      return;
    }
    if (!canSearch) return;

    setLoading(true);
    try {
      const res = await fetchOtSubmissionAuditReport({
        date_from: dateFrom,
        date_to: dateTo,
        source,
        status,
        employee_search: debouncedSearch || undefined,
      });

      if (res && res.success) {
        setRows(res.data || []);
        if (res.metrics) setMetrics(res.metrics);
      } else {
        showMessage("error", res?.message || "Failed to load OT submission audit report.");
      }
    } catch (err) {
      console.error("fetchOtSubmissionAuditReport error:", err);
      showMessage("error", err.message || "Server error loading OT submission audit report.");
    } finally {
      setLoading(false);
    }
  }, [tenant, canSearch, dateFrom, dateTo, source, status, debouncedSearch, showMessage]);

  useEffect(() => {
    if (canSearch) {
      loadReport();
    }
  }, [canSearch, loadReport]);

  // Excel Export
  const handleExportExcel = useCallback(async () => {
    if (!rows || rows.length === 0) {
      showMessage("warning", "No data available to export.");
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("OT Submissions Audit");

      // Title header
      worksheet.mergeCells("A1:K1");
      const titleCell = worksheet.getCell("A1");
      titleCell.value = `Overtime Submission & Source Audit Report (${dateFrom} to ${dateTo})`;
      titleCell.font = { name: "Arial", size: 14, bold: true, color: { argb: "FFFFFFFF" } };
      titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E293B" } };
      titleCell.alignment = { vertical: "middle", horizontal: "center" };

      // Table Headers
      worksheet.addRow([]);
      const headerRow = worksheet.addRow([
        "Shift Date",
        "Submission Date",
        "Employee No",
        "Staff Name",
        "Department",
        "Branch",
        "Source Channel",
        "Requested Hrs",
        "Approved Hrs",
        "Status",
        "Submitted By",
        "Reason / Remarks",
      ]);

      headerRow.eachCell((cell) => {
        cell.font = { name: "Arial", size: 10, bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF475569" } };
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });

      // Rows
      rows.forEach((r) => {
        worksheet.addRow([
          r.request_date ? String(r.request_date).split("T")[0] : "",
          r.submitted_at ? formatYangon(r.submitted_at) : "",
          r.employee_number || r.staff_code || "",
          r.staff_name || "",
          r.department_name || "",
          r.branch_name || "",
          r.source_label || r.source_code || "",
          Number(r.requested_hours || 0),
          Number(r.approved_hours || 0),
          String(r.status || "").toUpperCase(),
          r.created_by_name || "",
          r.reason || "",
        ]);
      });

      // Formatting & Widths
      worksheet.columns.forEach((col) => {
        col.width = 18;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `OT_Submission_Audit_${dateFrom}_to_${dateTo}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      showMessage("success", "Excel export downloaded successfully.");
    } catch (err) {
      console.error("Excel Export Error:", err);
      showMessage("error", "Failed to generate Excel file.");
    }
  }, [rows, dateFrom, dateTo, showMessage]);

  // PDF Export
  const handleExportPdf = useCallback(() => {
    if (!rows || rows.length === 0) {
      showMessage("warning", "No data available to export.");
      return;
    }

    try {
      const doc = new jsPDF("landscape");
      doc.setFontSize(14);
      doc.text(`Overtime Submission & Source Audit Report`, 14, 15);
      doc.setFontSize(10);
      doc.text(`Period: ${dateFrom} to ${dateTo} | Tenant: ${tenant}`, 14, 22);

      const tableData = rows.map((r) => [
        r.request_date ? String(r.request_date).split("T")[0] : "",
        r.submitted_at ? formatYangon(r.submitted_at) : "",
        r.employee_number || r.staff_code || "",
        r.staff_name || "",
        r.department_name || "",
        r.source_label || r.source_code || "",
        Number(r.requested_hours || 0).toFixed(1),
        Number(r.approved_hours || 0).toFixed(1),
        String(r.status || "").toUpperCase(),
        r.created_by_name || "",
      ]);

      doc.autoTable({
        startY: 28,
        head: [["Shift Date", "Submission Date", "Emp No", "Staff Name", "Department", "Source Channel", "Req Hrs", "App Hrs", "Status", "Submitted By"]],
        body: tableData,
        theme: "striped",
        styles: { fontSize: 8 },
        headStyles: { fillColor: [30, 41, 59] },
      });

      doc.save(`OT_Submission_Audit_${dateFrom}_to_${dateTo}.pdf`);
      showMessage("success", "PDF export downloaded successfully.");
    } catch (err) {
      console.error("PDF Export Error:", err);
      showMessage("error", "Failed to generate PDF document.");
    }
  }, [rows, dateFrom, dateTo, tenant, showMessage]);

  // AG-Grid Column Definitions
  const columnDefs = useMemo(() => [
    {
      headerName: "Shift Date",
      field: "request_date",
      width: 120,
      valueFormatter: (p) => (p.value ? String(p.value).split("T")[0] : "-"),
    },
    {
      headerName: "Submission Date",
      field: "submitted_at",
      width: 160,
      valueFormatter: (p) => (p.value ? formatYangon(p.value) : "-"),
    },
    {
      headerName: "Staff Info",
      field: "staff_name",
      width: 200,
      cellRenderer: (p) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e293b", lineHeight: 1.2 }}>
            {p.value}
          </Typography>
          <Typography variant="caption" sx={{ color: "#64748b" }}>
            {p.data?.employee_number || p.data?.staff_code || "-"}
          </Typography>
        </Box>
      ),
    },
    {
      headerName: "Department",
      field: "department_name",
      width: 150,
      valueFormatter: (p) => p.value || "General",
    },
    {
      headerName: "Source Channel",
      field: "source_label",
      width: 180,
      cellRenderer: (p) => {
        const code = p.data?.source_code || "MOBILE_CLAIM";
        const bg = sourceColor[code] || "#64748b";
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Chip
              label={p.value || code}
              size="small"
              sx={{
                bgcolor: `${bg}15`,
                color: bg,
                fontWeight: 700,
                fontSize: "0.75rem",
                border: `1px solid ${bg}40`,
              }}
            />
          </Box>
        );
      },
    },
    {
      headerName: "Req. Hrs",
      field: "requested_hours",
      width: 110,
      type: "numericColumn",
      valueFormatter: (p) => (p.value != null ? Number(p.value).toFixed(1) : "0.0"),
    },
    {
      headerName: "App. Hrs",
      field: "approved_hours",
      width: 110,
      type: "numericColumn",
      valueFormatter: (p) => (p.value != null ? Number(p.value).toFixed(1) : "0.0"),
    },
    {
      headerName: "Status",
      field: "status",
      width: 130,
      cellRenderer: (p) => {
        const s = String(p.value || "submitted").toLowerCase();
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Chip
              label={s.replaceAll("_", " ")}
              color={statusColor[s] || "default"}
              size="small"
              sx={{ fontWeight: 700, textTransform: "capitalize", fontSize: "0.75rem" }}
            />
          </Box>
        );
      },
    },
    {
      headerName: "Submitted By",
      field: "created_by_name",
      width: 150,
      valueFormatter: (p) => p.value || "System",
    },
    {
      headerName: "Reason / Remarks",
      field: "reason",
      flex: 1,
      minWidth: 200,
      valueFormatter: (p) => p.value || "-",
    },
  ], []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      <AppHeader />
      <Box sx={{ px: { xs: 2, sm: 4 }, py: 3, maxWidth: 1600, mx: "auto" }}>
        
        {/* Header Breadcrumb & Title */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/reports")}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
          >
            Back to Reports
          </Button>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
              Overtime Submission & Source Audit Report
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              Comprehensive audit trail of OT submissions across Mobile Claims, HR Direct Entries, and Auto-Capture.
            </Typography>
          </Box>
        </Stack>

        {/* Top Summary Metrics KPI Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>TOTAL CLAIMED OT</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e293b", mt: 0.5 }}>
                      {metrics.total_requested_hours} <Typography component="span" variant="caption">hrs</Typography>
                    </Typography>
                  </Box>
                  <TimerIcon sx={{ color: "#3b82f6", fontSize: 32 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>TOTAL APPROVED OT</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#10b981", mt: 0.5 }}>
                      {metrics.total_approved_hours} <Typography component="span" variant="caption">hrs</Typography>
                    </Typography>
                  </Box>
                  <ApprovedIcon sx={{ color: "#10b981", fontSize: 32 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>MOBILE APP CLAIMS</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#3b82f6", mt: 0.5 }}>
                      {metrics.mobile_claims_count} <Typography component="span" variant="caption">({metrics.mobile_claims_hours}h)</Typography>
                    </Typography>
                  </Box>
                  <MobileIcon sx={{ color: "#3b82f6", fontSize: 32 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>HR DIRECT ENTRIES</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#8b5cf6", mt: 0.5 }}>
                      {metrics.hr_manual_count} <Typography component="span" variant="caption">({metrics.hr_manual_hours}h)</Typography>
                    </Typography>
                  </Box>
                  <ManualIcon sx={{ color: "#8b5cf6", fontSize: 32 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>PENDING APPROVALS</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#f59e0b", mt: 0.5 }}>
                      {metrics.pending_count} <Typography component="span" variant="caption">items</Typography>
                    </Typography>
                  </Box>
                  <PendingIcon sx={{ color: "#f59e0b", fontSize: 32 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filter Control Bar */}
        <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0", mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Date From"
                type="date"
                fullWidth
                size="small"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Date To"
                type="date"
                fullWidth
                size="small"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Submission Source</InputLabel>
                <Select
                  value={source}
                  label="Submission Source"
                  onChange={(e) => setSource(e.target.value)}
                >
                  {SOURCES.map((s) => (
                    <MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {STATUSES.map((s) => (
                    <MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3.5}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search Staff Code / Name..."
                value={employeeSearch}
                onChange={(e) => setEmployeeSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2.5, pt: 2, borderTop: "1px dashed #e2e8f0" }}>
            <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600 }}>
              Showing {rows.length} records
            </Typography>
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<RefreshIcon />}
                onClick={loadReport}
                disabled={loading}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportExcel}
                disabled={loading || rows.length === 0}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
              >
                Export Excel
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PdfIcon />}
                onClick={handleExportPdf}
                disabled={loading || rows.length === 0}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
              >
                Export PDF
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Data Table */}
        <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #e2e8f0", bgcolor: "#ffffff" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <div className="ag-theme-quartz" style={{ height: 600, width: "100%" }}>
              <AgGridReact
                rowData={rows}
                columnDefs={columnDefs}
                pagination={true}
                paginationPageSize={20}
                animateRows={true}
                rowHeight={48}
              />
            </div>
          )}
        </Paper>
      </Box>

      {/* Notification Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%", borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
